'use client';
import React from 'react';
import { Table, Button, Group } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconPencil, IconTrash, IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { formatDateTime } from '../../utils/payment';
import { Leg } from '../../types/trip';
import { useTranslation } from 'react-i18next';
import { getTipoLabel } from '../../utils/tipoLabels';
import { parseIsoToTz } from '@/lib/dayjs';

type Props = {
  legs: Leg[];
  onEdit?: (index: number) => void;
  onDelete?: (index: number) => void;
  moveUp?: (index: number) => void;
  moveDown?: (index: number) => void;
  readOnly?: boolean;
};

export const LegsTable: React.FC<Props> = ({
  legs,
  onEdit,
  onDelete,
  moveUp,
  moveDown,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 950px)');

  // Check if we have action handlers - if not, we're in read-only mode
  const hasActions = !readOnly && (onEdit || onDelete || moveUp || moveDown);

  // Short date format for mobile: DD/MM HH:mm (without year)
  const formatShortDateTime = (iso: string): string => {
    const d = parseIsoToTz(iso);
    if (!d.isValid()) {
      return iso;
    }
    const day = `${d.date()}`.padStart(2, '0');
    const month = `${d.month() + 1}`.padStart(2, '0');
    const hours = `${d.hour()}`.padStart(2, '0');
    const minutes = `${d.minute()}`.padStart(2, '0');
    return `${day}/${month} ${hours}:${minutes}`;
  };

  // Short tipo format for mobile
  const formatShortTipo = (tipo: string): string => {
    return tipo === 'Internacional' ? 'Int.' : 'Nac.';
  };

  return (
    <Table striped withRowBorders highlightOnHover mt="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>#</Table.Th>
          <Table.Th>{t('inicio')}</Table.Th>
          {!isMobile && <Table.Th>{t('fin')}</Table.Th>}
          <Table.Th>{t('tipo')}</Table.Th>
          <Table.Th>{t('region')}</Table.Th>
          {hasActions && <Table.Th>{t('acciones')}</Table.Th>}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {legs.map((l, li) =>
          isMobile ? (
            // Mobile layout: separate rows for inicio and fin
            <React.Fragment key={`${l.inicio}-${li}`}>
              <Table.Tr>
                <Table.Td rowSpan={2}>{li + 1}</Table.Td>
                <Table.Td colSpan={2}>
                  <strong>{t('inicio')}:</strong> {formatShortDateTime(l.inicio)}
                </Table.Td>
                <Table.Td>{formatShortTipo(l.tipo)}</Table.Td>
                <Table.Td rowSpan={2}>{l.region ?? '-'}</Table.Td>
                {hasActions && (
                  <Table.Td rowSpan={2}>
                    <Group>
                      {onEdit && (
                        <Button
                          size="sm"
                          onClick={() => onEdit(li)}
                          aria-label={t('actualizarTramo')}
                        >
                          <IconPencil size={16} />
                        </Button>
                      )}
                      {onDelete && (
                        <Button
                          size="sm"
                          variant="subtle"
                          color="red"
                          onClick={() => onDelete(li)}
                          aria-label={t('eliminarTramo')}
                        >
                          <IconTrash size={16} />
                        </Button>
                      )}
                      {moveUp && (
                        <Button
                          size="sm"
                          variant="light"
                          disabled={li === 0}
                          onClick={() => moveUp(li)}
                          aria-label={t('moverArriba')}
                        >
                          <IconChevronUp size={16} />
                        </Button>
                      )}
                      {moveDown && (
                        <Button
                          size="sm"
                          variant="light"
                          disabled={li === legs.length - 1}
                          onClick={() => moveDown(li)}
                          aria-label={t('moverAbajo')}
                        >
                          <IconChevronDown size={16} />
                        </Button>
                      )}
                    </Group>
                  </Table.Td>
                )}
              </Table.Tr>
              <Table.Tr>
                <Table.Td colSpan={2}>
                  <strong>{t('fin')}:</strong> {formatShortDateTime(l.fin)}
                </Table.Td>
                <Table.Td />
              </Table.Tr>
            </React.Fragment>
          ) : (
            // Desktop layout: single row with all columns
            <Table.Tr key={`${l.inicio}-${li}`}>
              <Table.Td>{li + 1}</Table.Td>
              <Table.Td>{formatDateTime(l.inicio)}</Table.Td>
              <Table.Td>{formatDateTime(l.fin)}</Table.Td>
              <Table.Td>{getTipoLabel(l.tipo, t)}</Table.Td>
              <Table.Td>{l.region ?? '-'}</Table.Td>
              {hasActions && (
                <Table.Td>
                  <Group>
                    {onEdit && (
                      <Button
                        size="sm"
                        onClick={() => onEdit(li)}
                        aria-label={t('actualizarTramo')}
                      >
                        <IconPencil size={16} />
                      </Button>
                    )}
                    {onDelete && (
                      <Button
                        size="sm"
                        variant="subtle"
                        color="red"
                        onClick={() => onDelete(li)}
                        aria-label={t('eliminarTramo')}
                      >
                        <IconTrash size={16} />
                      </Button>
                    )}
                    {moveUp && (
                      <Button
                        size="sm"
                        variant="light"
                        disabled={li === 0}
                        onClick={() => moveUp(li)}
                        aria-label={t('moverArriba')}
                      >
                        <IconChevronUp size={16} />
                      </Button>
                    )}
                    {moveDown && (
                      <Button
                        size="sm"
                        variant="light"
                        disabled={li === legs.length - 1}
                        onClick={() => moveDown(li)}
                        aria-label={t('moverAbajo')}
                      >
                        <IconChevronDown size={16} />
                      </Button>
                    )}
                  </Group>
                </Table.Td>
              )}
            </Table.Tr>
          )
        )}
      </Table.Tbody>
    </Table>
  );
};

export default LegsTable;
